import React, { useRef } from 'react';
import * as XLSX from 'xlsx';
import Bugsnag from '@bugsnag/js';
import { faFileUpload } from '@fortawesome/pro-solid-svg-icons';
import Button from '../Button/Button';
import { UploadedEntity } from '../../components/DueDiligence/AddCompanyModal/AddCompanyModal';
import { ValidationErrorMessages } from '../../services/errorMessages';
import { formatNumber } from '../../services/utils';

// Define the structure for validation errors
export interface ValidationError {
    row: number;
    field: string;
    value: string;
    message: string | JSX.Element;
    headerMessage?: string;
}

interface IProps {
    /** Callback to update the list of successfully validated entities */
    setEntities: (entities: UploadedEntity[]) => void;
    /** Callback to update the list of validation errors */
    setErrors: (errors: ValidationError[]) => void;
    /** Callback to store the uploaded file for API submission */
    setUploadedFile?: (file: File | null) => void;
    /** Callback triggered when file processing starts - signals parent to show progress UI */
    onProcessingStart?: (totalRows: number) => void;
    /** Callback to update progress during file processing */
    onProgressUpdate?: (processedRows: number, totalRows: number) => void;
    /** Callback triggered when file processing completes */
    onProcessingComplete?: () => void;
    /** Callback triggered when processing is aborted by the user */
    onProcessingAborted?: () => void;
    /** Ref to expose abort method to parent component */
    abortRef?: React.MutableRefObject<(() => void) | null>;
    /** type of validation we are doing company or individual    */
    validationType: 'company' | 'individual';
}

const FileUploadComponent: React.FC<IProps> = ({
    setEntities,
    setErrors,
    setUploadedFile,
    onProcessingStart,
    onProgressUpdate,
    onProcessingComplete,
    onProcessingAborted,
    abortRef,
    validationType
}) => {
    // Reference to the hidden file input element
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Reference to track if processing should be aborted
    // This flag is checked during processing loops to stop execution
    const abortControllerRef = useRef<{ shouldAbort: boolean }>({ shouldAbort: false });

    // Reference to store the PapaParse parser instance for CSV parsing
    // This allows us to abort CSV parsing mid-stream
    const papaParseRef = useRef<any>(null);

    /**
     * Aborts the current file processing operation
     * This function is exposed to the parent component via abortRef
     * It sets the abort flag and cancels any active PapaParse operations
     */
    const abortProcessing = () => {
        // Set the abort flag to stop async processing loops
        abortControllerRef.current.shouldAbort = true;

        // If there's an active PapaParse CSV parsing operation, abort it
        if (papaParseRef.current) {
            papaParseRef.current.abort();
            papaParseRef.current = null;
        }

        // Clear the uploaded file reference since processing was aborted
        setUploadedFile?.(null);

        // Notify parent component that processing was aborted
        onProcessingAborted?.();
    };

    // Expose the abort method to parent component through the ref
    // This allows parent to call abort from outside this component
    React.useEffect(() => {
        if (abortRef) {
            abortRef.current = abortProcessing;
        }
    }, [abortRef]);

    /**
     * Validates a kennitala (Icelandic ID number)
     * Valid formats:
     * - 10 digits: ##########
     * - 11 characters with dash: ######-####
     */
    const validateKennitala = (value: string): boolean => {
        if (!value) return false;

        // Remove whitespace
        const trimmed = value.trim();

        // Check for 10 digit format
        if (/^\d{10}$/.test(trimmed)) {
            return true;
        }

        // Check for 11 character format with dash (######-####)
        if (/^\d{6}-\d{4}$/.test(trimmed)) {
            return true;
        }

        return false;
    };

    /**
     * Validates an email address using a standard regex pattern
     */
    const validateEmail = (email: string): boolean => {
        if (!email) return false;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
    };

    /**
     * Processes uploaded data asynchronously in chunks to maintain UI responsiveness
     * Returns validated entities and any validation errors found
     * Processing can be aborted mid-stream via abortControllerRef
     */
    const processDataAsync = async (data: any[]): Promise<{ valid: UploadedEntity[], errors: ValidationError[] }> => {
        const validEntities: UploadedEntity[] = [];
        const validationErrors: ValidationError[] = [];

        // Get the total number of rows to process (excluding empty rows)
        const totalRows = data.length;

        // Notify parent component that processing is starting
        onProcessingStart?.(totalRows);

        // Dynamically adjust chunk size based on dataset size
        // Larger datasets get bigger chunks to reduce overhead
        // Smaller datasets get smaller chunks for more frequent UI updates
        const CHUNK_SIZE = totalRows > 50000 ? 10000 : totalRows > 10000 ? 5000 : 1000;

        // Track when we last yielded to the browser (for UI updates)
        let lastYieldTime = Date.now();
        const YIELD_INTERVAL = 50; // Yield to browser every 50ms minimum

        for (let chunkStart = 0; chunkStart < data.length; chunkStart += CHUNK_SIZE) {
            // Check if processing should be aborted before processing next chunk
            // This allows users to cancel long-running operations
            if (abortControllerRef.current.shouldAbort) {
                return { valid: [], errors: [] };
            }

            // Get the current chunk of rows to process
            const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, data.length);
            const chunk = data.slice(chunkStart, chunkEnd);

            // Process each row in the current chunk
            chunk.forEach((row, chunkIndex) => {
                // Calculate the actual row index in the full dataset (0-based)
                const index = chunkStart + chunkIndex;


                // Extract values from the row - handle different possible header names
                // Try specific header names first, then fall back to column indices
                // we add '' as default to avoid undefined values so that .trim works later
                const kennitala: string = validationType === "company" ? row['Kennitala fyrirtækis'] || '' : row['Kennitala'] || '';
                const netfangUmsjonaradila: string = row['Netfang umsjónaraðila (val)'] || '';
                const netfang: string = row['Netfang'] || '';
                const kennitalaTengiliðs: string = row['Kennitala tengiliðs (val)'] || '';
                const netfangTengiliðs: string = row['Netfang tengiliðs (val)'] || '';

                // Validate required field: Kennitala (Icelandic national ID)
                // Must be present and pass kennitala format validation
                if (!validateKennitala(kennitala)) {
                    validationErrors.push({
                        row: index + 2, // +2 because index is 0-based and we skip header row
                        field: 'Kennitala',
                        value: kennitala,
                        message: kennitala.length === 0
                            ? ValidationErrorMessages.MissingKennitala
                            : ValidationErrorMessages.InvalidKennitalaFormat
                    });
                }

                // Validate optional field: Netfang (email address)
                // Only validate format if a value is provided
                if (netfang && !validateEmail(netfang)) {
                    validationErrors.push({
                        row: index + 2,
                        field: 'Netfang',
                        value: netfang,
                        message: netfang.length === 0
                            ? ValidationErrorMessages.MissingEmail
                            : ValidationErrorMessages.InvalidEmailFormat
                    });
                }

                // Only validate format if a value is provided
                if (netfangUmsjonaradila && !validateEmail(netfangUmsjonaradila)) {
                    validationErrors.push({
                        row: index + 2,
                        field: 'Netfang umsjónaraðila',
                        value: netfangUmsjonaradila,
                        message: netfangUmsjonaradila.length === 0
                            ? ValidationErrorMessages.MissingEmail
                            : ValidationErrorMessages.InvalidEmailFormat
                    });
                }

                // Validate optional field: Kennitala tengiliðs (contact person ID)
                // Only validate format if a value is provided
                if (kennitalaTengiliðs && !validateKennitala(kennitalaTengiliðs)) {
                    validationErrors.push({
                        row: index + 2,
                        field: 'Kennitala tengiliðs',
                        value: kennitalaTengiliðs,
                        message: kennitalaTengiliðs.length === 0
                            ? ValidationErrorMessages.MissingKennitala
                            : ValidationErrorMessages.InvalidKennitalaFormat
                    });
                }

                // Validate optional field: Netfang umsjonaraðila (email address)
                // Only validate format if a value is provided
                if (netfangTengiliðs && !validateEmail(netfangTengiliðs)) {
                    validationErrors.push({
                        row: index + 2,
                        field: 'Netfang tengiliðs',
                        value: netfangTengiliðs,
                        message: netfangTengiliðs.length === 0
                            ? ValidationErrorMessages.MissingEmail
                            : ValidationErrorMessages.InvalidEmailFormat
                    });
                }

                // Add this entity to the valid entities list
                // Store validation errors with the entity for later reference
                // Trim whitespace from all values to normalize data
                validEntities.push({
                    kennitala: kennitala.trim(),
                    netfang: netfang.trim(),
                    netfangUmsjonaradila: netfangUmsjonaradila.trim(),
                    kennitalaTengiliðs: kennitalaTengiliðs.trim(),
                    netfangTengiliðs: netfangTengiliðs.trim(),
                    error: validationErrors.filter(err => err.row === index + 2)
                });
            });

            // Update progress callback to inform parent component
            onProgressUpdate?.(chunkEnd, totalRows);

            // Only yield to browser if enough time has passed since last yield
            // This reduces the overhead while still keeping UI responsive
            const now = Date.now();
            if (now - lastYieldTime >= YIELD_INTERVAL) {
                // Allow the UI to update by yielding control back to the event loop
                // This creates a micro-task that allows React to re-render and handle user input
                await new Promise(resolve => setTimeout(resolve, 0));
                lastYieldTime = now;
            }
        }

        // Notify parent component that processing is complete
        onProcessingComplete?.();

        return { valid: validEntities, errors: validationErrors };
    };

    /**
     * Handles Excel file parsing using SheetJS (xlsx) library
     * Now uses async processing for better UI responsiveness
     * Can be aborted by calling abortProcessing()
     * Limits uploads to maximum 5000 rows for performance
     */
    const handleExcel = async (file: File) => {
        // Reset abort flag before starting new processing
        abortControllerRef.current.shouldAbort = false;

        // Store the file reference for later API submission
        setUploadedFile?.(file);

        try {
            // Read the file as an ArrayBuffer
            const arrayBuffer = await file.arrayBuffer();

            // Check if we should abort after reading file
            if (abortControllerRef.current.shouldAbort) {
                console.log('Excel parsing aborted');
                return;
            }

            // Parse the Excel file
            const workbook = XLSX.read(
                arrayBuffer, {
                type: 'array',
                raw: true, // Keep all values as strings when possible
                cellText: false, // Don't convert to formatted text
                cellNF: false, //preserve number formats

            });

            // Get the first worksheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            // Convert worksheet to JSON format with headers
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                raw: false, // Get formatted text
                defval: ''  // Default value for empty cells
            });

            // Validate row count limit before processing
            // Maximum allowed rows to prevent performance issues
            const MAX_ROWS = 5000;
            if (jsonData.length > MAX_ROWS) {
                // Show error message to user about row limit
                setErrors([{
                    row: 0,
                    field: 'Skrá',
                    value: file.name,
                    message: `Skjalið inniheldur of margar línur. Hámark er ${MAX_ROWS} línur, en skjalið inniheldur ${formatNumber(jsonData.length, '-', 0)} línur. Vinsamlegast skiptu skjalinu upp í minni skrár.`
                }]);

                // Clear entities since file is invalid
                setEntities([]);

                // Notify completion so UI can update
                onProcessingComplete?.();

                return;
            }

            // Process and validate the data asynchronously
            const { valid, errors: validationErrors } = await processDataAsync(jsonData);

            // Only update state if processing wasn't aborted
            if (!abortControllerRef.current.shouldAbort) {
                setEntities(valid);
                setErrors(validationErrors);
            }
        } catch (error) {
            // This catch block handles unexpected errors during Excel file processing
            // These could be: file reading errors, corrupted Excel files, or SheetJS parsing errors
            // All of these are system errors that should be reported to Bugsnag
            console.error('Error parsing Excel:', error);

            // Report Excel parsing/processing errors to Bugsnag
            Bugsnag.notify(error as Error, (event) => {
                event.context = 'FileUploadComponent.handleExcel';
                event.addMetadata('file', {
                    name: file.name,
                    size: file.size,
                    type: file.type
                });
                // Add additional context if available
                if (error instanceof Error) {
                    event.addMetadata('error', {
                        message: error.message,
                        stack: error.stack
                    });
                }
            });

            // Ensure we notify completion even on error
            onProcessingComplete?.();
        }
    };

    /**
     * Handles file selection from the input element
     * Determines file type and routes to appropriate parser (CSV or Excel)
     */
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Determine file type by extension
        const fileName = file.name.toLowerCase();
        if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
            handleExcel(file);
        }

        // Reset the input so the same file can be selected again if needed
        event.target.value = '';
    };

    /**
     * Triggers the hidden file input click event
     */
    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className=''>
            {/* Hidden file input that only accepts CSV and Excel files */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />

            {/* Visible button that triggers file input */}
            <Button
                icon={faFileUpload}
                buttonType='secondary'
                onClick={handleButtonClick}
            >
                Hlaða upp skjali
            </Button>
        </div>
    );
};

export default FileUploadComponent;
